/* eslint-disable */


import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { sortByName } from "../lib/utils";
import { QRCodeCanvas } from "qrcode.react";
import { pdf } from "@react-pdf/renderer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { fmtDate } from "../lib/format";
import { Download, Printer, Tag, History } from "lucide-react";
import { toast } from "sonner";
import { LABEL_FORMATS, getSettings, loadSettings, DEFAULT_FORMAT_ID } from "../lib/printer";
import { showPdfPreview } from "../components/PdfPreview";
import { normalizeEAN13 } from "../lib/ean13";
import { Link } from "react-router-dom";
import { Barcode, canvasToDataUrl, svgToDataUrl, generateBarcodeDataUrl, generateQrDataUrl } from "../components/Barcode";
import LabelPdf, { getSpiceLevel, PEPPER_BODY, PEPPER_CAP, PEPPER_STEM } from "../components/LabelPdf";

const PreviewPeppers = ({ level, size }) => (
  <span className="inline-flex shrink-0 mt-1" data-testid="label-preview-peppers" data-level={level}>
    {Array.from({ length: level }, (_, i) => (
      <svg key={i} viewBox="0 0 24 24" width={size} height={size} style={{ marginLeft: i ? -1 : 0 }}>
        <path d={PEPPER_BODY} fill="#D0021B" />
        <path d={PEPPER_CAP} fill="#2E7D32" />
        <path d={PEPPER_STEM} stroke="#2E7D32" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      </svg>
    ))}
  </span>
);
import { COMPANY_HEADER } from "../lib/company";

// Composants extraits dans /components/Barcode.jsx et /components/LabelPdf.jsx (iter22 refactor)

export default function Labels() {
  const [params] = useSearchParams();
  const [settings, setSettings] = useState(getSettings());
  const [products, setProducts] = useState([]);
  const [lots, setLots] = useState([]);
  const [orders, setOrders] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [productId, setProductId] = useState("");
  const [lotId, setLotId] = useState("");
  // orderLineKey = "<orderId>::<lineId>" pour cibler une ligne précise d'un OF multi-produits
  const [orderLineKey, setOrderLineKey] = useState("");
  const [copies, setCopies] = useState(1);
  const [formatId, setFormatId] = useState(LABEL_FORMATS[settings.default_format] ? settings.default_format : DEFAULT_FORMAT_ID);
  const [showQR, setShowQR] = useState(true);
  const [showBarcode, setShowBarcode] = useState(true);
  const [showAllergens, setShowAllergens] = useState(true);
  const [showIngredients, setShowIngredients] = useState(true);
  const [showSpice, setShowSpice] = useState(true);
  // "inline" = sur l'étiquette principale · "separate" = 2e étiquette dédiée · "none" = caché
  const [nutritionMode, setNutritionMode] = useState("inline");
  // Mode "lecture vitrée" : polices agrandies pour lecture à travers la vitre
  // d'un distributeur automatique. La hauteur AUTO du format s'adapte en conséquence.
  const [largeFont, setLargeFont] = useState(false);

  // Phase 87ao — Décalage X/Y à l'impression (compensation imprimante/rouleau)
  // Sauvegardé en localStorage pour rester par poste/imprimante. Sans toucher au driver.
  const [printOffsetX, setPrintOffsetX] = useState(() => {
    const v = parseFloat(localStorage.getItem("labels_print_offset_x") || "0");
    return isNaN(v) ? 0 : v;
  });
  const [printOffsetY, setPrintOffsetY] = useState(() => {
    const v = parseFloat(localStorage.getItem("labels_print_offset_y") || "0");
    return isNaN(v) ? 0 : v;
  });
  useEffect(() => { localStorage.setItem("labels_print_offset_x", String(printOffsetX)); }, [printOffsetX]);
  useEffect(() => { localStorage.setItem("labels_print_offset_y", String(printOffsetY)); }, [printOffsetY]);

  const qrRef = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get("/products"),
      api.get("/lots"),
      api.get("/distributors"),
      api.get("/orders"),
    ]).then(([p, l, d, o]) => {
      setProducts(p.data); setLots(l.data); setDistributors(d.data); setOrders(o.data);
      const urlOrder = params.get("order");
      const urlLot = params.get("lot");
      if (urlOrder) {
        const ord = o.data.find((x) => x.id === urlOrder);
        if (ord && ord.lines?.length > 0) {
          // Sélectionne la première ligne par défaut
          const firstLine = ord.lines[0];
          setOrderLineKey(`${ord.id}::${firstLine.id}`);
          setProductId(firstLine.product_id);
          setCopies(Math.max(1, Math.round(firstLine.quantity_target || 1)));
          return;
        }
      }
      if (urlLot) {
        const lot = l.data.find((x) => x.id === urlLot);
        if (lot) { setProductId(lot.product_id); setLotId(lot.id); }
      }
    });
    loadSettings({ withLogo: true }).then((s) => setSettings(s)).catch(() => {});
  }, []);

  const product = useMemo(() => products.find((p) => p.id === productId), [products, productId]);
  const lot = useMemo(() => lots.find((l) => l.id === lotId), [lots, lotId]);

  // Phase 87x (16/06/2026) — Si on arrive sur /etiquettes?order=XXX, on filtre la
  // liste déroulante "Produit" pour ne montrer QUE les produits de l'OF en cours,
  // au lieu de la liste exhaustive du catalogue. Évite à l'opérateur de scroller
  // 100+ produits pour retrouver ceux qu'il vient de fabriquer.
  const urlOrderId = params.get("order");
  const filteredProducts = useMemo(() => {
    if (!urlOrderId) return products;
    const ord = orders.find((o) => o.id === urlOrderId);
    if (!ord || !ord.lines?.length) return products;
    const orderPids = new Set(ord.lines.map((ln) => ln.product_id).filter(Boolean));
    return products.filter((p) => orderPids.has(p.id));
  }, [urlOrderId, orders, products]);

  // Décompose la clé OF::ligne pour retrouver l'OF et la ligne sélectionnée
  const { order, orderLine } = useMemo(() => {
    if (!orderLineKey) return { order: null, orderLine: null };
    const [oid, lid] = orderLineKey.split("::");
    const ord = orders.find((o) => o.id === oid);
    if (!ord) return { order: null, orderLine: null };
    const line = (ord.lines || []).find((ln) => ln.id === lid);
    return { order: ord, orderLine: line || null };
  }, [orderLineKey, orders]);

  // Si un OF est sélectionné, ses infos prennent le pas sur le lot pour l'étiquette
  const labelLotNumber = orderLine?.lot_number || lot?.lot_number || "";

  // Phase 87aa (17/06/2026) — DLC calculée on-the-fly si non encore stockée
  // (cas typique : impression d'étiquettes pour un OF en statut PLANIFIE, avant
  // clôture, donc pas encore de lot ni de DLC sur la ligne).
  // Formule : production_date + product.shelf_life_days
  // Source de la date de production (par priorité décroissante) :
  //   1. lot.production_date (lot déjà créé, OF clôturé)
  //   2. order.actual_production_date (saisie rétroactive, fabrication d'hier)
  //   3. order.completed_at (date de clôture OF)
  //   4. order.planned_date (date planifiée à la création OF — cas auto-OF Phase 87x)
  //   5. aujourd'hui (fallback ultime)
  const computedProductionDate = useMemo(() => {
    // Phase 101el (Feb 2026) — Priorité stricte à la date EFFECTIVE de
    // fabrication pour respecter la traçabilité HACCP. `planned_date` est
    // volontairement écartée : si l'OF n'est pas encore terminé et qu'aucune
    // date réelle n'est saisie, on retombe sur aujourd'hui (jour d'impression)
    // plutôt que sur la date planifiée qui peut être fausse. Cas d'usage :
    // Alain fabrique souvent la nuit, les dates prévues peuvent chevaucher
    // la date réelle.
    if (lot?.production_date) return lot.production_date.slice(0, 10);
    if (order?.actual_production_date) return order.actual_production_date.slice(0, 10);
    if (order?.completed_at) return order.completed_at.slice(0, 10);
    return new Date().toISOString().slice(0, 10);
  }, [lot, order]);

  const computedDlc = useMemo(() => {
    if (orderLine?.dlc) return orderLine.dlc;
    if (lot?.dlc) return lot.dlc;
    const product = products.find((p) => p.id === productId);
    const shelfDays = product?.shelf_life_days;
    if (!shelfDays || !computedProductionDate) return "";
    const d = new Date(computedProductionDate + "T00:00:00");
    if (Number.isNaN(d.getTime())) return "";
    d.setDate(d.getDate() + Number(shelfDays));
    return d.toISOString().slice(0, 10);
  }, [orderLine, lot, productId, products, computedProductionDate]);

  const labelDlc = computedDlc;
  const labelProductionDate = computedProductionDate;
  const depotInfo = useMemo(() => {
    const d = distributors.find((x) => x.id === lot?.distributor_id);
    return d ? { name: d.name, code: d.code } : { name: "", code: "" };
  }, [distributors, lot]);
  const depotName = depotInfo.name;
  const positionLabel = useMemo(() => {
    if (!lot?.emplacement_number) return "";
    const prefix = depotInfo.code || depotInfo.name || "";
    const pos = `E${lot.emplacement_number}${lot.barquette_index ? `·B${lot.barquette_index}` : ""}`;
    return prefix ? `${prefix} · ${pos}` : pos;
  }, [lot, depotInfo]);
  const productLots = useMemo(() => lots.filter((l) => !productId || l.product_id === productId), [lots, productId]);
  // Liste plate "OF + ligne" pour le sélecteur
  const orderLineOptions = useMemo(() => {
    const opts = [];
    for (const o of orders) {
      for (const ln of (o.lines || [])) {
        if (!ln.lot_number) continue;
        if (productId && ln.product_id !== productId) continue;
        const p = products.find((x) => x.id === ln.product_id);
        opts.push({
          key: `${o.id}::${ln.id}`,
          label: `${o.reference} — ${p?.name || "?"} (lot ${ln.lot_number})`,
        });
      }
    }
    return opts;
  }, [orders, products, productId]);
  const baseFormat = LABEL_FORMATS[formatId] || LABEL_FORMATS[DEFAULT_FORMAT_ID];

  // Calcule une hauteur adaptée au contenu si le format est "AUTO".
  // En mode largeFont, les marges intègrent les polices agrandies.
  const computeAutoHeight = () => {
    if (baseFormat.height != null) return baseFormat.height;
    if (!product) return largeFont ? 130 : 120;
    const k = largeFont ? 1.3 : 1; // facteur d'expansion vertical pour le mode agrandi
    let h = 60 * k;
    if (product.net_weight != null && product.net_weight !== "") h += 6 * k;
    if (showAllergens && product.allergens?.length > 0) h += 6 * k;
    if (showIngredients && product.ingredients) {
      h += 6 * k + Math.ceil(product.ingredients.length / (largeFont ? 50 : 55)) * 3 * k;
    }
    if (nutritionMode === "inline" && product.nutrition && Object.values(product.nutrition).some((v) => v != null && v !== "")) {
      const rows = Object.values(product.nutrition).filter((v) => v != null && v !== "").length;
      h += 10 * k + rows * 2.5 * k;
    }
    if (product.storage_instructions) h += 5 * k + Math.ceil(product.storage_instructions.length / (largeFont ? 45 : 50)) * 3 * k;
    if (product.reheating_instructions) h += 5 * k + Math.ceil(product.reheating_instructions.length / (largeFont ? 45 : 50)) * 3 * k;
    if (depotName || positionLabel) h += 4 * k;
    return Math.min(largeFont ? 360 : 300, Math.max(largeFont ? 130 : 100, Math.ceil(h)));
  };
  const format = { ...baseFormat, height: computeAutoHeight() };

  // Phase 87ai — EAN13 IMMUABLE par produit (recopié manuellement dans Nayax).
  // Le code-barres ne dépend JAMAIS du lot pour garantir que les caisses revendeurs
  // reconnaissent toujours le même produit. Si le produit n'a pas d'ean13_base
  // (12 chiffres GS1), on désactive le code-barres et on affiche un avertissement
  // pour forcer l'opérateur à attribuer un EAN13 stable depuis la fiche produit.
  const hasStableEan13 = !!(product?.ean13_base && product.ean13_base.replace(/\D/g, "").length === 12);
  const barcodeValue = hasStableEan13 ? normalizeEAN13(product.ean13_base) : "";
  // QR pointe vers la page publique de traçabilité du lot.
  // URL stable, lisible, scannable par n'importe quel téléphone (sans app dédiée).
  // Cible : https://maison-thai.ch/trace/{sku}/{lot_number}
  // Fallback si pas de lot : on encode juste le produit (utile pour les étiquettes
  // d'étalage / vitrine sans lot précis).
  const qrPayload = (() => {
    // Préfère l'URL publique de production (maison-thai.ch) plutôt que l'URL preview,
    // car les étiquettes sont scannées par les clients finaux. Si le domaine prod
    // n'est pas connu, on retombe sur l'origin courant.
    const PUBLIC_BASE = "https://maison-thai.ch";
    const sku = (product?.sku || "").trim();
    if (!sku) return PUBLIC_BASE;
    const lot = (labelLotNumber || "").trim();
    return lot
      ? `${PUBLIC_BASE}/trace/${encodeURIComponent(sku)}/${encodeURIComponent(lot)}`
      : `${PUBLIC_BASE}/trace/${encodeURIComponent(sku)}`;
  })();

  // Inject dynamic @page size for browser print
  useEffect(() => {
    const id = "dynamic-print-style";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("style");
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = `
      @page { size: ${format.width}mm ${format.height}mm; margin: 0; }
      @media print {
        html, body {
          width: ${format.width}mm !important;
          height: ${format.height}mm !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
        }
        .print-area {
          width: ${format.width}mm !important;
          height: ${format.height}mm !important;
          margin: 0 !important;
          padding: 0 !important;
          /* Phase 87ao — décalage compensation imprimante (sans modifier le driver) */
          transform: translate(${printOffsetX}mm, ${printOffsetY}mm);
          transform-origin: top left;
        }
        /* Force la taille physique exacte de l'étiquette : surcharge l'inline style en pixels */
        [data-testid="label-preview"] {
          width: ${format.width}mm !important;
          height: ${format.height}mm !important;
          box-sizing: border-box !important;
          border-width: 0 !important;
          padding: 2mm !important;
        }
      }
    `;
    return () => { if (el) el.textContent = ""; };
  }, [format, printOffsetX, printOffsetY]);

  const handlePrint = () => {
    if (!product) { toast.error("Sélectionnez un produit"); return; }
    logPrintHistory("PRINT");
    window.print();
  };

  const logPrintHistory = async (action) => {
    try {
      await api.post("/print-history", {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        lot_number: labelLotNumber || "",
        dlc: labelDlc || "",
        production_date: labelProductionDate || "",
        format_id: format.id,
        format_label: `${format.width}×${format.height}mm`,
        copies: Number(copies) || 1,
        action,
        order_id: order?.id || null,
        order_reference: order?.reference || "",
        lot_id: lot?.id || null,
        ean13: String(barcodeValue || ""),
      });
    } catch {
      // Log silencieux — ne pas bloquer l'impression
    }
  };

  const handlePdf = async () => {
    if (!product) { toast.error("Sélectionnez un produit"); return; }
    const qrCanvas = qrRef.current?.querySelector("canvas");
    const barSvg = barRef.current?.querySelector("svg");
    const qrDataUrl = showQR && qrCanvas ? await canvasToDataUrl(qrCanvas) : null;
    const barDataUrl = showBarcode && barSvg ? await svgToDataUrl(barSvg) : null;

    const nutritionRows = [];
    if (nutritionMode !== "none" && product.nutrition) {
      const n = product.nutrition;
      const energy = [];
      if (n.energy_kj != null) energy.push(`${n.energy_kj} kJ`);
      if (n.energy_kcal != null) energy.push(`${n.energy_kcal} kcal`);
      if (energy.length) nutritionRows.push({ label: "Énergie", value: energy.join(" / ") });
      if (n.fat != null) nutritionRows.push({ label: "Matières grasses", value: `${n.fat} g` });
      if (n.saturated_fat != null) nutritionRows.push({ label: "  dont acides gras saturés", value: `${n.saturated_fat} g` });
      if (n.carbs != null) nutritionRows.push({ label: "Glucides", value: `${n.carbs} g` });
      if (n.sugars != null) nutritionRows.push({ label: "  dont sucres", value: `${n.sugars} g` });
      if (n.proteins != null) nutritionRows.push({ label: "Protéines", value: `${n.proteins} g` });
      if (n.salt != null) nutritionRows.push({ label: "Sel", value: `${n.salt} g` });
    }
    const labelData = {
      name: product.name, sku: product.sku,
      spice_level: showSpice ? getSpiceLevel(product) : 0,
      net_weight: product.net_weight,
      net_weight_unit: product.net_weight_unit || "g",
      net_weight_detail: product.net_weight_detail || "",
      lot_number: labelLotNumber || "—",
      unit: product.unit,
      depot: depotName,
      position: positionLabel,
      production_date: labelProductionDate ? fmtDate(labelProductionDate) : "",
      dlc: labelDlc ? fmtDate(labelDlc) : "",
      allergens: showAllergens ? (product.allergens || []) : [],
      ingredients: showIngredients ? (product.ingredients || "") : "",
      nutrition: nutritionRows,
      storage_instructions: product.storage_instructions || "",
      reheating_instructions: product.reheating_instructions || "",
      qrDataUrl, barDataUrl,
    };
    const labels = Array.from({ length: Number(copies) || 1 }, () => labelData);
    // Construit une adresse multi-lignes complète : Rue / CH-NPA Ville / Tél
    const addressLines = [];
    if (settings.company_address) addressLines.push(settings.company_address);
    const cityLine = [
      settings.company_country ? `${settings.company_country}-${settings.company_postal_code || ""}`.trim() : (settings.company_postal_code || ""),
      settings.company_city || "",
    ].filter(Boolean).join(" ").trim();
    if (cityLine) addressLines.push(cityLine);
    if (settings.company_phone) addressLines.push(`Tél ${settings.company_phone}`);
    const fullAddress = addressLines.join("\n");

    const blob = await pdf(<LabelPdf labels={labels} format={format} company={COMPANY_HEADER} companyAddress={fullAddress} companyLogo={settings.company_logo} legalMentions={settings.legal_mentions || ""} nutritionMode={nutritionMode} />).toBlob();
    const filename = `etiquettes-${format.id}-${product.sku}${lot ? `-${lot.lot_number}` : ""}.pdf`;
    showPdfPreview(blob, filename);
    logPrintHistory("PDF");
    toast.success(`PDF ${format.width}×${format.height}mm généré`);
  };

  // Phase 101ek (Feb 2026) — Génère TOUTES les étiquettes de l'OF sélectionné
  // dans un seul PDF (utile pour un OF revendeur avec beaucoup de lignes).
  // Pour chaque ligne : produit N étiquettes = `quantity_target` de la ligne.
  const [generatingAll, setGeneratingAll] = useState(false);
  const handleGenerateAllOrderLabels = async () => {
    if (!order) { toast.error("Sélectionnez un OF"); return; }
    const orderLines = (order.lines || []).filter(l => l.lot_number);
    if (!orderLines.length) {
      toast.error("Aucune ligne avec lot dans cet OF");
      return;
    }
    // Phase 101el — Refuse si l'OF n'a pas encore de date effective de fabrication.
    // On ne veut jamais imprimer d'étiquettes avec une date PRÉVISIONNELLE.
    if (!order.actual_production_date && !order.completed_at) {
      toast.error("Cet OF n'a pas de date effective de fabrication. Terminez l'OF (ou saisissez la date réelle) avant d'imprimer les étiquettes.");
      return;
    }
    setGeneratingAll(true);
    try {
      // Adresse société (identique à handlePdf)
      const addressLines = [];
      if (settings.company_address) addressLines.push(settings.company_address);
      const cityLine = [
        settings.company_country ? `${settings.company_country}-${settings.company_postal_code || ""}`.trim() : (settings.company_postal_code || ""),
        settings.company_city || "",
      ].filter(Boolean).join(" ").trim();
      if (cityLine) addressLines.push(cityLine);
      if (settings.company_phone) addressLines.push(`Tél ${settings.company_phone}`);
      const fullAddress = addressLines.join("\n");

      // Phase 101gc.1 (Feb 2026) — Bug fix : avant, seul le produit courant
      // avait son QR/barcode rendu (via le DOM). Résultat : les étiquettes
      // des autres produits de l'OF sortaient SANS code-barre EAN ni QR.
      // On génère maintenant QR + barcode PAR PRODUIT via les helpers off-DOM
      // (generateBarcodeDataUrl / generateQrDataUrl).
      const PUBLIC_BASE = "https://maison-thai.ch";

      const allLabels = [];
      let totalCount = 0;

      console.log("[label-batch] Génération de", orderLines.length, "produit(s) pour", order.reference);
      for (const ln of orderLines) {
        const p = products.find(x => x.id === ln.product_id);
        if (!p) {
          console.warn("[label-batch] Produit archivé ou introuvable, ligne ignorée:", ln.product_id);
          continue;
        }
        const perLine = Math.max(1, Math.round(ln.quantity_target || 1));
        totalCount += perLine;

        // Phase 101el — Date de production STRICTEMENT effective (HACCP).
        // On ne fait JAMAIS retomber sur planned_date. Si aucune date réelle,
        // fallback sur aujourd'hui (jour d'impression, cas edge uniquement).
        const prodDate = order.actual_production_date?.slice(0, 10)
          || order.completed_at?.slice(0, 10)
          || new Date().toISOString().slice(0, 10);
        let dlcVal = ln.dlc;
        if (!dlcVal && p.shelf_life_days && prodDate) {
          const d = new Date(prodDate + "T00:00:00");
          if (!isNaN(d.getTime())) {
            d.setDate(d.getDate() + Number(p.shelf_life_days));
            dlcVal = d.toISOString().slice(0, 10);
          }
        }

        const nutritionRows = [];
        if (nutritionMode !== "none" && p.nutrition) {
          const n = p.nutrition;
          const energy = [];
          if (n.energy_kj != null) energy.push(`${n.energy_kj} kJ`);
          if (n.energy_kcal != null) energy.push(`${n.energy_kcal} kcal`);
          if (energy.length) nutritionRows.push({ label: "Énergie", value: energy.join(" / ") });
          if (n.fat != null) nutritionRows.push({ label: "Matières grasses", value: `${n.fat} g` });
          if (n.saturated_fat != null) nutritionRows.push({ label: "  dont acides gras saturés", value: `${n.saturated_fat} g` });
          if (n.carbs != null) nutritionRows.push({ label: "Glucides", value: `${n.carbs} g` });
          if (n.sugars != null) nutritionRows.push({ label: "  dont sucres", value: `${n.sugars} g` });
          if (n.proteins != null) nutritionRows.push({ label: "Protéines", value: `${n.proteins} g` });
          if (n.salt != null) nutritionRows.push({ label: "Sel", value: `${n.salt} g` });
        }

        // Phase 101gc.1 — Génère QR et EAN13 par produit (off-DOM).
        // Avant ce fix, seul le produit affiché à l'écran avait son
        // barcode/QR rendu ; les autres produits de l'OF sortaient sans code.
        const productHasEan13 = !!(p.ean13_base && p.ean13_base.replace(/\D/g, "").length === 12);
        const productBarcode = productHasEan13 ? normalizeEAN13(p.ean13_base) : "";
        const productBarDataUrl = showBarcode && productBarcode
          ? await generateBarcodeDataUrl(productBarcode, { height: 50, width: 1.8, fontSize: 11 })
          : null;

        const sku = (p.sku || "").trim();
        const lotStr = (ln.lot_number || "").trim();
        const productQrPayload = sku
          ? (lotStr
              ? `${PUBLIC_BASE}/trace/${encodeURIComponent(sku)}/${encodeURIComponent(lotStr)}`
              : `${PUBLIC_BASE}/trace/${encodeURIComponent(sku)}`)
          : PUBLIC_BASE;
        const productQrDataUrl = showQR
          ? await generateQrDataUrl(productQrPayload, { width: 128, margin: 1 })
          : null;

        const labelData = {
          name: p.name, sku: p.sku,
          spice_level: showSpice ? getSpiceLevel(p) : 0,
          net_weight: p.net_weight,
          net_weight_unit: p.net_weight_unit || "g",
          net_weight_detail: p.net_weight_detail || "",
          lot_number: ln.lot_number || "—",
          unit: p.unit,
          depot: "", position: "",
          production_date: prodDate ? fmtDate(prodDate) : "",
          dlc: dlcVal ? fmtDate(dlcVal) : "",
          allergens: showAllergens ? (p.allergens || []) : [],
          ingredients: showIngredients ? (p.ingredients || "") : "",
          nutrition: nutritionRows,
          storage_instructions: p.storage_instructions || "",
          reheating_instructions: p.reheating_instructions || "",
          qrDataUrl: productQrDataUrl,
          barDataUrl: productBarDataUrl,
        };
        for (let i = 0; i < perLine; i++) allLabels.push(labelData);
      }

      if (!allLabels.length) {
        toast.error("Impossible de générer les étiquettes (aucun produit valide)");
        return;
      }

      const blob = await pdf(
        <LabelPdf
          labels={allLabels}
          format={format}
          company={COMPANY_HEADER}
          companyAddress={fullAddress}
          companyLogo={settings.company_logo}
          legalMentions={settings.legal_mentions || ""}
          nutritionMode={nutritionMode}
        />
      ).toBlob();
      const filename = `etiquettes-OF-${order.reference}.pdf`;
      showPdfPreview(blob, filename);

      // Phase 101gc.4 (Feb 2026) — Trace chaque ligne de l'OF dans
      // l'historique d'impression (fire-and-forget, une entrée par produit).
      const prodDateFinal = order.actual_production_date?.slice(0, 10)
        || order.completed_at?.slice(0, 10)
        || new Date().toISOString().slice(0, 10);
      await Promise.all(orderLines.map(async (ln) => {
        const p = products.find(x => x.id === ln.product_id);
        if (!p) return;
        const perLine = Math.max(1, Math.round(ln.quantity_target || 1));
        const productHasEan13 = !!(p.ean13_base && p.ean13_base.replace(/\D/g, "").length === 12);
        const productBarcode = productHasEan13 ? normalizeEAN13(p.ean13_base) : "";
        let dlcVal = ln.dlc;
        if (!dlcVal && p.shelf_life_days && prodDateFinal) {
          const d = new Date(prodDateFinal + "T00:00:00");
          if (!isNaN(d.getTime())) {
            d.setDate(d.getDate() + Number(p.shelf_life_days));
            dlcVal = d.toISOString().slice(0, 10);
          }
        }
        try {
          await api.post("/print-history", {
            product_id: p.id,
            product_name: p.name,
            product_sku: p.sku || "",
            lot_number: ln.lot_number || "",
            dlc: dlcVal || "",
            production_date: prodDateFinal || "",
            format_id: format.id,
            format_label: `${format.width}×${format.height}mm`,
            copies: perLine,
            action: "PDF",
            order_id: order.id,
            order_reference: order.reference || "",
            lot_id: null,
            ean13: productBarcode,
          });
        } catch {
          // Fire-and-forget : le trou d'historique ne doit pas casser l'impression
        }
      }));

      toast.success(`${totalCount} étiquette(s) générée(s) pour ${order.reference} (${orderLines.length} produit${orderLines.length > 1 ? "s" : ""})`);
    } catch (e) {
      toast.error("Erreur lors de la génération : " + (e?.message || "inconnue"));
    } finally {
      setGeneratingAll(false);
    }
  };

  // Aspect for preview (1 mm ≈ 3px for screen preview)
  const previewScale = 3;
  const previewWidth = format.width * previewScale;
  const previewHeight = format.height * previewScale;
  const isNarrow = format.width <= 80;

  return (
    <div className="space-y-4" data-testid="labels-page">
      <div className="flex justify-end no-print">
        <Link to="/etiquettes/historique">
          <Button variant="outline" size="sm" data-testid="print-history-btn">
            <History className="w-4 h-4 mr-2" /> Historique des impressions
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Configuration */}
      <div className="lg:col-span-2 space-y-4 no-print">
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-[#1F4E3D]" />
            <h2 className="font-heading text-xl font-medium">Configuration</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Format d'étiquette</Label>
              <Select value={formatId} onValueChange={setFormatId}>
                <SelectTrigger data-testid="label-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(LABEL_FORMATS).map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#575A57] mt-1.5 font-mono">
                {format.width} × {format.height} mm — {format.printer}
              </p>
            </div>
            <div>
              <Label className="mb-1.5 block">
                Produit
                {urlOrderId && filteredProducts.length < products.length && (
                  <span className="ml-2 text-xs font-normal text-[#1F4E3D]">
                    · filtré sur l&apos;OF ({filteredProducts.length} produit{filteredProducts.length > 1 ? "s" : ""})
                  </span>
                )}
              </Label>
              <Select value={productId} onValueChange={(v) => { setProductId(v); setLotId(""); setOrderLineKey(""); }}>
                <SelectTrigger data-testid="label-product"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>{sortByName(filteredProducts).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Ordre de fabrication / ligne (prioritaire)</Label>
              <Select value={orderLineKey || "none"} onValueChange={(v) => {
                const val = v === "none" ? "" : v;
                setOrderLineKey(val);
                if (val) {
                  const [oid, lid] = val.split("::");
                  const o = orders.find((x) => x.id === oid);
                  const ln = o?.lines?.find((x) => x.id === lid);
                  if (ln) {
                    setProductId(ln.product_id);
                    setLotId("");
                    setCopies(Math.max(1, Math.round(ln.quantity_target || 1)));
                  }
                }
              }}>
                <SelectTrigger data-testid="label-order"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {orderLineOptions.map((opt) => (
                    <SelectItem key={opt.key} value={opt.key}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#575A57] mt-1.5">
                Pour les OF multi-produits, sélectionnez la ligne souhaitée. Le n° de lot, la DLC et le nombre d'exemplaires sont pré-remplis automatiquement.
              </p>
              {/* Phase 101ek — Bouton pour générer TOUTES les étiquettes de l'OF en un seul PDF.
                  Utile pour un OF revendeur avec beaucoup de lignes différentes (19 produits × 2 = 38 étiquettes). */}
              {order && (order.lines || []).filter(l => l.lot_number).length > 1 && (
                <div className="mt-2.5 flex items-center gap-2 flex-wrap p-2.5 bg-[#EAF4EE] border border-[#C5DBCF] rounded">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-[#1F4E3D]">
                      {order.reference} · {(order.lines || []).filter(l => l.lot_number).length} produits
                    </div>
                    <div className="text-[10px] text-[#575A57] mt-0.5">
                      {(order.lines || []).filter(l => l.lot_number).reduce((s, l) => s + Math.round(l.quantity_target || 1), 0)} étiquettes au total (même lot, DLC, date fab.)
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleGenerateAllOrderLabels}
                    disabled={generatingAll}
                    className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white text-xs"
                    data-testid="generate-all-of-labels-btn"
                    title="Génère un seul PDF avec toutes les étiquettes de l'OF"
                  >
                    <Tag className="w-3.5 h-3.5 mr-1.5" /> {generatingAll ? "Génération…" : "Toutes les étiquettes de l'OF"}
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label className="mb-1.5 block">Lot existant (optionnel)</Label>
              <Select value={lotId || "none"} onValueChange={(v) => setLotId(v === "none" ? "" : v)} disabled={!!orderLineKey}>
                <SelectTrigger data-testid="label-lot"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {productLots.map((l) => <SelectItem key={l.id} value={l.id}>Lot {l.lot_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">
                Nombre d'exemplaires
                {orderLine && (
                  <span className="ml-2 text-xs font-normal text-[#1F4E3D]">
                    · {Math.round(orderLine.quantity_target || 0)} barquette{(orderLine.quantity_target || 0) > 1 ? "s" : ""}
                  </span>
                )}
              </Label>
              <Input type="number" min="1" max="500" value={copies} onChange={(e) => setCopies(e.target.value)} data-testid="label-copies" />
            </div>
            <div className="space-y-3 pt-2 border-t border-[#F0F0EB]">
              <div className="flex items-center gap-2">
                <Checkbox id="qr" checked={showQR} onCheckedChange={setShowQR} data-testid="toggle-qr" />
                <Label htmlFor="qr" className="cursor-pointer">Afficher le QR Code</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="bar" checked={showBarcode} onCheckedChange={setShowBarcode} data-testid="toggle-ean13" />
                <Label htmlFor="bar" className="cursor-pointer">Afficher le code-barres (EAN13)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="al" checked={showAllergens} onCheckedChange={setShowAllergens} data-testid="toggle-allergens" />
                <Label htmlFor="al" className="cursor-pointer">Afficher les allergènes</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="ing" checked={showIngredients} onCheckedChange={setShowIngredients} data-testid="toggle-ingredients" />
                <Label htmlFor="ing" className="cursor-pointer">Afficher les ingrédients</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="spice" checked={showSpice} onCheckedChange={setShowSpice} data-testid="toggle-spice" />
                <Label htmlFor="spice" className="cursor-pointer">
                  Afficher les piments (niveau d&apos;épice)
                  {product && <span className="ml-1 text-[11px] font-normal text-[#575A57]" data-testid="spice-level-info">— niveau {getSpiceLevel(product)}/3</span>}
                </Label>
              </div>
              <div className="flex items-center gap-2 p-2 bg-[#FBF4E9] border border-[#E8D3A8] rounded">
                <Checkbox id="lg" checked={largeFont} onCheckedChange={setLargeFont} data-testid="toggle-large-font" />
                <Label htmlFor="lg" className="cursor-pointer text-[#8C6B2F]">
                  Polices agrandies <span className="text-[11px] font-normal">(lecture à travers une vitre de distributeur)</span>
                </Label>
              </div>
              <div className="space-y-1.5">
                <Label className="block">Valeurs nutritionnelles</Label>
                <Select value={nutritionMode} onValueChange={setNutritionMode}>
                  <SelectTrigger data-testid="nutrition-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inline">Sur la même étiquette</SelectItem>
                    <SelectItem value="separate">Sur une étiquette séparée (2 étiquettes par exemplaire)</SelectItem>
                    <SelectItem value="none">Ne pas imprimer</SelectItem>
                  </SelectContent>
                </Select>
                {nutritionMode === "separate" && (
                  <p className="text-[11px] text-[#575A57] mt-1">
                    Chaque exemplaire générera 2 pages PDF : l'étiquette produit, puis une étiquette dédiée aux valeurs nutritionnelles.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 pt-3">
              <Button onClick={handlePdf} className="flex-1 bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="download-pdf-btn">
                <Download className="w-4 h-4 mr-2" /> PDF
              </Button>
              <Button onClick={handlePrint} variant="outline" className="flex-1" data-testid="print-btn">
                <Printer className="w-4 h-4 mr-2" /> Imprimer
              </Button>
            </div>
            {/* Phase 87ao — Décalage compensation imprimante */}
            <div className="bg-[#FBF4E9] border border-[#E8D9B8] rounded p-3 space-y-2">
              <Label className="text-[11px] uppercase tracking-wider text-[#8C6B2F] font-semibold">
                Décalage impression (compensation rouleau / imprimante)
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="off-x" className="text-[10px] text-[#575A57]">Horizontal X (mm)</Label>
                  <Input
                    id="off-x"
                    type="number"
                    step="0.5"
                    min="-10"
                    max="10"
                    value={printOffsetX}
                    onChange={(e) => setPrintOffsetX(parseFloat(e.target.value) || 0)}
                    className="h-8 font-mono"
                    data-testid="print-offset-x"
                  />
                </div>
                <div>
                  <Label htmlFor="off-y" className="text-[10px] text-[#575A57]">Vertical Y (mm)</Label>
                  <Input
                    id="off-y"
                    type="number"
                    step="0.5"
                    min="-10"
                    max="10"
                    value={printOffsetY}
                    onChange={(e) => setPrintOffsetY(parseFloat(e.target.value) || 0)}
                    className="h-8 font-mono"
                    data-testid="print-offset-y"
                  />
                </div>
              </div>
              <p className="text-[10px] text-[#575A57] leading-snug">
                Réglage par poste (sauvegardé localement). Valeurs positives = vers la droite / vers le bas.
                Ex : <span className="font-mono">+2</span> pour pousser de 2 mm à droite. N&apos;affecte pas le driver.
              </p>
            </div>
            <div className="text-xs text-[#575A57] bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3">
              Imprimante configurée : <span className="font-mono text-[#1A1C1A]">{settings.printer_name}</span>
              {settings.printer_ip && <span className="font-mono"> — {settings.printer_ip}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="lg:col-span-3">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57] mb-3 no-print flex items-center gap-2">
          Aperçu — <span className="font-mono normal-case tracking-normal">{format.width}×{format.height}mm</span>
        </div>
        <div className="print-area" style={{ width: `${previewWidth}px` }}>
          {product && !hasStableEan13 && (
            <div className="mb-3 bg-[#FCEEEA] border-2 border-[#8B1A1A] rounded-lg p-3 text-[#8B1A1A]" data-testid="label-missing-ean13-banner">
              <div className="font-bold text-sm flex items-center gap-2">
                <span className="text-lg">⚠</span> EAN13 manquant pour ce produit
              </div>
              <div className="text-xs mt-1.5 leading-snug">
                Le code-barres ne sera <strong>pas imprimé</strong> tant qu'un EAN13 stable n'est pas attribué.
                Ouvrez la fiche produit <Link to="/produits" className="underline font-semibold">Produits</Link> et assignez un EAN13 (préfixe GS1 ou code fournisseur).
                Cet EAN13 est <strong>immuable</strong> et utilisé par les caisses revendeurs / Nayax — il doit être figé avant le premier lot.
              </div>
            </div>
          )}
          {!product ? (
            <div className="bg-white border border-dashed border-[#E5E5E0] rounded-lg p-16 text-center text-[#575A57]">
              Sélectionnez un produit pour générer une étiquette.
            </div>
          ) : (
            <div
              className="bg-white p-2 overflow-hidden"
              data-testid="label-preview"
              style={{ width: `${previewWidth}px`, height: `${previewHeight}px`, fontSize: largeFont ? "13px" : "10px" }}
            >
              <div className="flex items-center gap-2">
                {settings.company_logo && (
                  <img src={settings.company_logo} alt="Logo" className={`${largeFont ? "w-9 h-9" : "w-7 h-7"} object-contain shrink-0`} />
                )}
                <div className="min-w-0">
                  <div className={`${largeFont ? "text-[11px]" : "text-[8px]"} uppercase tracking-widest text-[#575A57] font-semibold`}>{COMPANY_HEADER}</div>
                  {settings.company_address && (
                    <div className={`${largeFont ? "text-[10px]" : "text-[7px]"} text-[#575A57] whitespace-pre-line leading-tight mt-0.5`}>{settings.company_address}</div>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-1">
                <div className={`font-heading font-bold ${largeFont ? "text-[22px] mt-1.5" : "text-[17px] mt-1"} leading-tight text-[#8B1A1A]`}>{product.name}</div>
                {showSpice && getSpiceLevel(product) > 0 && <PreviewPeppers level={getSpiceLevel(product)} size={largeFont ? 20 : 16} />}
              </div>
              <div className={`${largeFont ? "text-[10px]" : "text-[8px]"} font-mono text-[#575A57]`}>SKU: {product.sku}</div>
              {product.net_weight != null && product.net_weight !== "" && (
                <div className={`mt-0.5 ${largeFont ? "text-[13px]" : "text-[10px]"} font-bold text-[#1A1C1A]`}>
                  Poids net : {product.net_weight} {product.net_weight_unit || "g"}
                  {product.net_weight_detail ? (
                    <span className="font-normal text-[#575A57]"> ({product.net_weight_detail})</span>
                  ) : null}
                </div>
              )}
              {showAllergens && product.allergens?.length > 0 && (
                <div className={`mt-1 ${largeFont ? "text-[11px]" : "text-[8px]"} text-[#8C6B2F] font-bold`}>
                  <span className="uppercase tracking-wider">Allergènes : </span>
                  {product.allergens.join(", ")}
                </div>
              )}
              {showIngredients && product.ingredients && (
                <div className={`mt-0.5 ${largeFont ? "text-[10px]" : "text-[7px]"} text-[#3A3A3A] leading-snug`}>
                  <span className="uppercase tracking-wider font-bold">Ingrédients : </span>
                  {product.ingredients}
                </div>
              )}
              {nutritionMode === "inline" && product.nutrition && Object.values(product.nutrition).some((v) => v != null && v !== "") && (
                <div className={`mt-1 border border-[#1A1C1A] rounded-sm ${largeFont ? "p-1.5 text-[10px]" : "p-1 text-[7px]"} text-[#1A1C1A]`}>
                  <div className={`uppercase tracking-wider font-bold mb-0.5 ${largeFont ? "text-[10px]" : ""}`}>Valeurs nutritionnelles · pour 100 g</div>
                  {[
                    product.nutrition.energy_kj != null || product.nutrition.energy_kcal != null
                      ? { l: "Énergie", v: [product.nutrition.energy_kj != null ? `${product.nutrition.energy_kj} kJ` : null, product.nutrition.energy_kcal != null ? `${product.nutrition.energy_kcal} kcal` : null].filter(Boolean).join(" / ") }
                      : null,
                    product.nutrition.fat != null ? { l: "Matières grasses", v: `${product.nutrition.fat} g` } : null,
                    product.nutrition.saturated_fat != null ? { l: "  dont saturés", v: `${product.nutrition.saturated_fat} g` } : null,
                    product.nutrition.carbs != null ? { l: "Glucides", v: `${product.nutrition.carbs} g` } : null,
                    product.nutrition.sugars != null ? { l: "  dont sucres", v: `${product.nutrition.sugars} g` } : null,
                    product.nutrition.proteins != null ? { l: "Protéines", v: `${product.nutrition.proteins} g` } : null,
                    product.nutrition.salt != null ? { l: "Sel", v: `${product.nutrition.salt} g` } : null,
                  ].filter(Boolean).map((row, i) => (
                    <div key={i} className={`flex justify-between ${largeFont ? "leading-snug" : "leading-tight"}`}>
                      <span className="text-[#3A3A3A]">{row.l}</span>
                      <span className="font-bold">{row.v}</span>
                    </div>
                  ))}
                </div>
              )}
              {product.storage_instructions && (
                <div className={`mt-1 ${largeFont ? "text-[11px]" : "text-[8px]"} text-[#1A1C1A] leading-snug`}>
                  <span className="uppercase tracking-wider font-bold text-[#1F4E3D]">Conservation : </span>
                  {product.storage_instructions}
                </div>
              )}
              {product.reheating_instructions && (
                <div className={`mt-0.5 ${largeFont ? "text-[11px]" : "text-[8px]"} text-[#1A1C1A] leading-snug`}>
                  <span className="uppercase tracking-wider font-bold text-[#1F4E3D]">Réchauffage : </span>
                  {product.reheating_instructions}
                </div>
              )}
              {settings.legal_mentions && (
                <div className={`mt-1.5 ${largeFont ? "text-[9px]" : "text-[7px]"} text-[#575A57] italic leading-snug whitespace-pre-line`} data-testid="label-legal-mentions">
                  {settings.legal_mentions}
                </div>
              )}
              <div className="border-t border-[#1A1C1A] my-1.5"></div>
              <div className="flex items-baseline gap-2">
                <div className={`${largeFont ? "text-[10px]" : "text-[7px]"} uppercase tracking-wider text-[#575A57] font-semibold`}>Lot :</div>
                <div className={`font-mono font-bold ${largeFont ? "text-[15px]" : "text-[11px]"}`}>{labelLotNumber || "—"}</div>
              </div>
              <div className="flex justify-between mt-1.5">
                <div>
                  <div className={`${largeFont ? "text-[10px]" : "text-[7px]"} uppercase tracking-wider text-[#575A57] font-semibold`}>Prod.</div>
                  <div className={largeFont ? "text-[12px]" : "text-[9px]"}>{fmtDate(labelProductionDate)}</div>
                </div>
                <div>
                  <div className={`${largeFont ? "text-[10px]" : "text-[7px]"} uppercase tracking-wider text-[#575A57] font-semibold`}>DLC</div>
                  <div className={largeFont ? "text-[12px]" : "text-[9px]"}>{fmtDate(labelDlc)}</div>
                </div>
              </div>
              <div className={`mt-2 flex ${isNarrow ? "flex-col items-center gap-2" : "items-center justify-between gap-3"}`}>
                {showQR && (
                  <div ref={qrRef}><QRCodeCanvas value={qrPayload} size={isNarrow ? (largeFont ? 64 : 50) : (largeFont ? 72 : 56)} level="M" includeMargin={false} /></div>
                )}
                {showBarcode && hasStableEan13 && (
                  <div ref={barRef} className={isNarrow ? "" : "flex-1 flex justify-end"}>
                    <Barcode value={barcodeValue} height={isNarrow ? (largeFont ? 34 : 26) : (largeFont ? 44 : 34)} width={largeFont ? 1.3 : 1.1} fontSize={largeFont ? 13 : 11} margin={0} />
                  </div>
                )}
                {showBarcode && !hasStableEan13 && (
                  <div className={`${isNarrow ? "" : "flex-1"} text-center text-[#8B1A1A] border border-dashed border-[#E8C1B4] bg-[#FCEEEA] rounded ${largeFont ? "text-[10px] px-2 py-1.5" : "text-[8px] px-1.5 py-1"} no-print-when-missing`} data-testid="label-missing-ean13-inline">
                    ⚠ EAN13 manquant — voir avertissement ci-dessus
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
